import { provideNdbxIcons } from '@allianz/ngx-ndbx/icon';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { EnvironmentProviders, importProvidersFrom, inject, provideAppInitializer, Provider } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideAngularQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { OAuthService, provideOAuthClient } from 'angular-oauth2-oidc';
import Cookies from 'js-cookie';
import { lastValueFrom } from 'rxjs';

import {
  PREFERRED_LANGUAGE_COOKIE_DURATION_IN_DAYS,
  PREFERRED_LANGUAGE_COOKIE_NAME,
} from '#components/locale-selector/locale-selector.component';
import { MockInterceptor } from '#e2e-tests/mock-interceptor';
import { setMockScenariosCookieFromQueryParam } from '#e2e-utils/mockScenariosCookieUtils';
import MockOauthService from '#services/user/mock-oauth.service';
import MockUserService from '#services/user/mock-user.service';
import UserService from '#services/user/user.service';

import { AppRouting } from './app-routing.module';
import { AuthInterceptor } from './http-interceptors/auth/auth-interceptor';
import { SessionExpiredInterceptor } from './http-interceptors/session-expired/session-expired-interceptor';

const interceptors: (Provider | EnvironmentProviders)[] = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: SessionExpiredInterceptor,
    multi: true,
  },
];
if (process.env['NODE_ENV'] === 'development') {
  void import('./utils/MockDate').then(({ mockDayjs }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).dayjs = mockDayjs;
  });
  interceptors.push({
    provide: HTTP_INTERCEPTORS,
    useClass: MockInterceptor,
    multi: true,
  });
  interceptors.push({ provide: UserService, useClass: MockUserService });
  interceptors.push({ provide: OAuthService, useClass: MockOauthService });
  setMockScenariosCookieFromQueryParam();
  Cookies.set(PREFERRED_LANGUAGE_COOKIE_NAME, 'master');
}

const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (http: HttpClient) =>
  new TranslateHttpLoader(http, '/assets/locales/', '.json');

export function appTranslateInitializer() {
  const translateService = inject(TranslateService);

  translateService.addLangs(['de-DE', 'en-GB', 'en-US', 'fr-FR', 'it-IT', 'master']);
  translateService.setDefaultLang('master');
  const cookieStoredLang = Cookies.get(PREFERRED_LANGUAGE_COOKIE_NAME) ?? 'en-GB';
  Cookies.set(PREFERRED_LANGUAGE_COOKIE_NAME, cookieStoredLang, {
    expires: PREFERRED_LANGUAGE_COOKIE_DURATION_IN_DAYS,
  });
  let langToUse;
  if (translateService.langs.includes(cookieStoredLang)) {
    langToUse = cookieStoredLang;
  } else {
    langToUse = 'en-GB';
  }
  return lastValueFrom(translateService.use(langToUse));
}

export const appConfig = {
  providers: [
    importProvidersFrom(AppRouting),
    provideAnimations(),
    provideNdbxIcons(),
    provideHttpClient(withInterceptorsFromDi()),
    provideOAuthClient(),
    provideAngularQuery(new QueryClient()),
    importProvidersFrom([
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ]),
    provideAppInitializer(appTranslateInitializer),
    ...interceptors,
  ],
};
